import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { FileText } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinOpsCopyHoverMenu from '@/shein-platform/components/SheinOpsCopyHoverMenu';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import SheinDateTimePicker from '@/shein-platform/modules/products/components/SheinDateTimePicker';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import useSheinTemplateScope from '@/shein-platform/hooks/useSheinTemplateScope';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import SheinTemplateScopeTabs from '@/shein-platform/components/SheinTemplateScopeTabs';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { usePlatformShopPick, resolveWriteShopId } from '@/hooks/usePlatformShopPick';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { copyTemplateName } from '@/shein-platform/utils/sheinTemplateCopyName';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import {
  buildSheinWorkspaceBatchOpsMenu,
  buildTemplateCopyMenuOptions,
  isTeamTemplateScope,
  resolveCopyTargetScope,
  resolveTemplateRowShopId,
  rowCanCopyScopedTemplate,
  SHEIN_TEMPLATE_SCOPES,
} from '@/shein-platform/constants/sheinTemplateScope';
import { showToast } from '@/utils/toast';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import CertificateBindProductModal from '@/shein-platform/modules/certificates/CertificateBindProductModal';
import '@/styles/shein-platform/shein-product-ops.css';

/** 平台 status：1 待生效、2 已生效、3 已失效、4 待填写、5 即将失效、6 草稿 */
const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: '1', label: '待生效' },
  { value: '2', label: '已生效' },
  { value: '3', label: '已失效' },
  { value: '4', label: '待填写' },
  { value: '5', label: '即将失效' },
  { value: '6', label: '草稿' },
];

const STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

const ACCEPT_FILES = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const MAX_FILE_BYTES = 20 * 1024 * 1024;

function parseTypeOptions(res) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  return Array.isArray(list) ? list.filter((item) => Number(item?.certificateTypeId) !== 844) : [];
}

function parseSchemaType(res, typeId) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  if (!Array.isArray(list) || list.length === 0) return null;
  if (typeId == null || typeId === '') return list[0];
  const id = Number(typeId);
  return list.find((item) => Number(item?.certificateTypeId) === id) || list[0];
}

function parseSrmAgencies(res) {
  const list = res?.srmDetectionAgencyList
    || res?.data?.srmDetectionAgencyList
    || res?.info?.srmDetectionAgencyList
    || [];
  return Array.isArray(list) ? list : [];
}

function extractSearchCertList(res) {
  const list = res?.data
    || res?.list
    || res?.info?.data
    || res?.info?.list
    || (Array.isArray(res) ? res : null);
  return Array.isArray(list) ? list : [];
}

function findCertByPoolSn(res, poolSn) {
  if (!poolSn) return null;
  const target = String(poolSn).trim();
  return extractSearchCertList(res).find((row) => String(row?.poolSn || '').trim() === target) || null;
}

/** 供 SheinDateTimePicker：统一为 yyyy-MM-dd HH:mm:ss */
function toPickerDatetimeValue(value) {
  return toPlatformDatetime(value);
}

function toPlatformDatetime(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (raw.includes('T')) {
    const base = raw.replace('T', ' ');
    return base.length === 16 ? `${base}:00` : base;
  }
  return raw.length === 16 ? `${raw}:00` : raw;
}

function isSrmField(field) {
  return String(field?.sourceFrom || '').toUpperCase() === 'SRM';
}

/** 去掉 label 右侧括号说明，如「检测机构 (SRM的)」→「检测机构」 */
function cleanFieldLabel(text) {
  return String(text || '')
    .replace(/\s*\*\s*$/g, '')
    .replace(/\s*[（(][^）)]*[）)]\s*$/g, '')
    .trim();
}

function FieldLabel({ children, required = false }) {
  return (
    <span className="spo-field__label">
      {children}
      {required ? <span className="spo-field__required" aria-hidden="true">*</span> : null}
    </span>
  );
}

function typeOptionLabel(item) {
  return item?.certificateType || item?.certificateTypeName || String(item?.certificateTypeId || '');
}

function typeCodeOf(item) {
  return item?.certificateTypeCode || item?.certificateTypeCodes || item?.typeCode || '';
}

function statusLabel(status, row) {
  if (row?.localTemplate && row?.canSyncToShein) return i18n.t('certLib.pendingSubmit', { ns: 'workbench' });
  if (status == null || status === '') return '—';
  const statusKey = STATUS_LABELS[String(status)] ? `certLib.st${status}` : '';
  return statusKey ? i18n.t(statusKey, { ns: 'workbench', defaultValue: STATUS_LABELS[String(status)] }) : String(status);
}

function statusModifier(status) {
  const s = Number(status);
  if (s === 2) return 'success';
  if (s === 3 || s === 4) return 'failed';
  if (s === 5) return 'pending';
  return 'pending';
}

function dimensionLabel(dim) {
  if (Number(dim) === 1) return i18n.t('certLib.partialGoods', { ns: 'workbench' });
  if (Number(dim) === 2) return i18n.t('certLib.allGoods', { ns: 'workbench' });
  return '—';
}

/** 列表来源：官方复制待提交 / 平台已同步 */
function isOfficialSourceRow(row) {
  if (!row) return false;
  if (row.localTemplate) return true;
  return String(row.source || '').toLowerCase() === 'official';
}

function canSubmitOfficialRow(row) {
  return Boolean(row?.localTemplate && row?.canSyncToShein);
}

function sourceLabel(row, { isOfficialTab = false } = {}) {
  if (isOfficialTab) return i18n.t('certLib.sourceOfficial', { ns: 'workbench' });
  return isOfficialSourceRow(row) ? i18n.t('certLib.sourceOfficial', { ns: 'workbench' }) : i18n.t('certLib.sourcePlatform', { ns: 'workbench' });
}

function bindSkcLabel(row) {
  const count = Number(row?.bindSkcCount);
  if (Number.isFinite(count) && count > 0) return i18n.t('certLib.boundCount', { ns: 'workbench', count });
  if (Number(row?.bindSkcFlag) === 1) return i18n.t('certLib.bound', { ns: 'workbench' });
  if (Number(row?.bindSkcFlag) === 0) return i18n.t('certLib.unbound', { ns: 'workbench' });
  return '—';
}

function formatTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('zh-CN', { hour12: false });
}

function fileNamesOf(row) {
  const files = Array.isArray(row?.fileList) ? row.fileList : [];
  if (files.length) {
    return files.map((f) => f.fileName || f.fileUrl || i18n.t('certLib.fileFallback', { ns: 'workbench' })).filter(Boolean);
  }
  if (row?.fileName) return [row.fileName];
  return [];
}

function isAllowedCertFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const okExt = name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
  const type = String(file.type || '').toLowerCase();
  const okType = !type
    || type === 'application/pdf'
    || type === 'image/png'
    || type === 'image/jpeg'
    || type === 'image/jpg';
  return okExt && okType;
}

function extractUploadInfo(res) {
  const info = res?.info && typeof res.info === 'object' ? res.info : res;
  return {
    fileUrl: info?.certificateUrl || info?.fileUrl || res?.certificateUrl || '',
    fileMd5: info?.certificateMd5 || info?.fileMd5 || res?.certificateMd5 || '',
  };
}

function extractSavePoolSn(res) {
  return res?.poolSn
    || res?.info?.poolSn
    || res?.data?.poolSn
    || '';
}

function buildPresetPayload(schema, values, srmValues = {}) {
  const presets = [
    ...(Array.isArray(schema?.presetInfoList) ? schema.presetInfoList : []),
    ...(Array.isArray(schema?.otherPresetInfoList) ? schema.otherPresetInfoList : []),
  ];
  const out = [];
  for (const field of presets) {
    const presetId = field?.presetId;
    if (presetId == null) continue;
    if (Number(field.inputType) === 5) continue;
    const key = String(presetId);
    const required = Number(field.isRequired) === 1;
    const label = field.presetRemark || field.presetName || presetId;

    if (isSrmField(field)) {
      const srm = srmValues?.[key] || {};
      const agencyId = srm.agencyId;
      const laboratoryId = srm.laboratoryId;
      if (!agencyId || !laboratoryId) {
        if (required) throw new Error(i18n.t('certLib.pickField', { ns: 'workbench', label }));
        continue;
      }
      out.push({
        presetId,
        valueList: [{
          valueId: Number(agencyId),
          value: Number(laboratoryId),
        }],
      });
      continue;
    }

    const raw = values?.[key];
    const inputType = Number(field.inputType);
    if (raw == null || String(raw).trim() === '') {
      if (required) throw new Error(i18n.t('certLib.fillField', { ns: 'workbench', label }));
      continue;
    }
    if (inputType === 1 || inputType === 2) {
      out.push({
        presetId,
        valueList: [{ valueId: Number(raw) }],
      });
    } else if (inputType === 4) {
      out.push({
        presetId,
        valueList: [{ value: toPlatformDatetime(raw) }],
      });
    } else {
      out.push({
        presetId,
        valueList: [{ value: String(raw).trim() }],
      });
    }
  }
  return out;
}

const TABLE_COLUMNS = [
  { key: 'check', label: '', width: '50px' },
  { key: 'poolSn', label: '证书编号' },
  { key: 'type', get label() { return fixedUi("证书类型"); } },
  { key: 'files', get label() { return fixedUi("证书文件"); } },
  { key: 'source', get label() { return fixedUi("来源"); }, width: '80px' },
  { key: 'status', get label() { return fixedUi("状态"); }, width: '100px' },
  { key: 'dimension', get label() { return fixedUi("适用范围"); }, width: '100px' },
  { key: 'bindSkc', label: '绑定 SKC', width: '110px' },
  { key: 'time', get label() { return fixedUi("时间"); } },
  { key: 'updatedAt', label: '平台更新' },
  { key: 'ops', get label() { return fixedUi("操作"); }, width: '240px' },
];

const COL_SPAN = TABLE_COLUMNS.length;

function rowKey(row) {
  if (row?.localTemplate) return `local-${row.id}`;
  return String(row?.poolSn || row?.id || '');
}

function FileDetailModal({ open, row, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !row) return null;

  const files = Array.isArray(row.fileList) ? row.fileList : [];

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-platform-cert-detail-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-platform-cert-detail-title">{t('certLib.fileTitle', { name: row.poolSn || row.name || '—' })}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          <p className="spo-batch-modal__hint">
            {row.certificateTypeName || row.certificateTypeCode || '—'}
            {' · '}
            {statusLabel(row.status, row)}
            {' · '}
            {dimensionLabel(row.certificateDimension)}
          </p>
          <div className="spo-drafts__table-wrap">
            <table className="spo-publish__table spo-drafts__table spo-certs-table">
              <thead>
                <tr>
                  <th className="spo-certs-th">{t('certLib.fileName')}</th>
                  <th className="spo-certs-th">{t('certLib.colOps')}</th>
                </tr>
              </thead>
              <tbody>
                {files.length === 0 ? (
                  <tr><td colSpan={2} className="spo-drafts__empty-cell">{t('certLib.noFiles')}</td></tr>
                ) : (
                  files.map((file, idx) => (
                    <tr key={`${file.fileMd5 || file.fileUrl || idx}`}>
                      <td className="spo-certs-td">{file.fileName || '—'}</td>
                      <td className="spo-certs-td">
                        {file.fileUrl ? (
                          <a
                            className="spo-btn spo-btn--secondary spo-btn--sm"
                            href={file.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('certLib.open')}
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function applySchemaToForm(typeSchema, seed, isEdit, setters) {
  const {
    setSchema,
    setTypeId,
    setTypeCode,
    setDimension,
    setPresetValues,
    setSrmValues,
  } = setters;
  setSchema(typeSchema || null);
  if (!typeSchema) {
    setPresetValues({});
    setSrmValues({});
    return;
  }
  if (typeSchema.certificateTypeId != null) {
    setTypeId(String(typeSchema.certificateTypeId));
  }
  const code = typeCodeOf(typeSchema);
  if (code) setTypeCode(String(code));
  if (typeSchema.certificateDimension != null && !isEdit) {
    setDimension(String(typeSchema.certificateDimension));
  }
  const next = {};
  const nextSrm = {};
  const presets = [
    ...(Array.isArray(typeSchema?.presetInfoList) ? typeSchema.presetInfoList : []),
    ...(Array.isArray(typeSchema?.otherPresetInfoList) ? typeSchema.otherPresetInfoList : []),
  ];
  const payloadPresets = Array.isArray(seed?.payload?.presetInfoList)
    ? seed.payload.presetInfoList
    : Array.isArray(seed?.presetInfoList) ? seed.presetInfoList : [];
  for (const field of presets) {
    const presetId = field?.presetId;
    if (presetId == null) continue;
    const key = String(presetId);
    const matched = payloadPresets.find((p) => Number(p?.presetId) === Number(presetId));
    const first = matched?.valueList?.[0];
    if (!first) continue;
    if (isSrmField(field)) {
      nextSrm[key] = {
        agencyId: first.valueId != null ? String(first.valueId) : '',
        laboratoryId: first.value != null ? String(first.value) : '',
      };
      continue;
    }
    if (first.valueId != null) {
      next[key] = String(first.valueId);
    } else if (first.value != null) {
      next[key] = Number(field.inputType) === 4
        ? toPickerDatetimeValue(first.value)
        : String(first.value);
    }
  }
  setPresetValues(next);
  setSrmValues(nextSrm);
}

function SaveCertificateModal({
  open,
  mode,
  shopId,
  typeOptions: parentTypeOptions,
  seed,
  onClose,
  onDone,
}) {
  const { t } = useTranslation('workbench');
  const fileInputRef = useRef(null);
  const [typeId, setTypeId] = useState('');
  const [typeCode, setTypeCode] = useState('');
  /** 已加载的完整填写规则缓存：typeId -> schema，切换类型时命中则不再请求。 */
  const schemaCacheRef = useRef(new Map());
  const [dimension, setDimension] = useState('1');
  const [fileName, setFileName] = useState('');
  const [schema, setSchema] = useState(null);
  const [schemaTypes, setSchemaTypes] = useState([]);
  const [srmAgencies, setSrmAgencies] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [syncingSchemas, setSyncingSchemas] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [presetValues, setPresetValues] = useState({});
  const [srmValues, setSrmValues] = useState({});
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const isEdit = mode === 'edit';
  const busy = saving || uploading || verifying || syncingSchemas;
  const title = isEdit ? t('certLib.editTitle', { no: seed?.poolSn || '' }) : t('certLib.createTitle');

  const typeOptions = useMemo(() => {
    if (schemaTypes.length) return schemaTypes;
    return Array.isArray(parentTypeOptions) ? parentTypeOptions : [];
  }, [schemaTypes, parentTypeOptions]);

  const typeSelectOptions = useMemo(
    () => typeOptions.map((item) => {
      const code = typeCodeOf(item);
      const name = typeOptionLabel(item);
      return {
        value: String(item.certificateTypeId),
        label: code ? `${name}（${code}）` : name,
      };
    }),
    [typeOptions],
  );

  /** 把响应中的完整填写规则放进缓存，并回填类型下拉 / 检测机构。 */
  const rememberSchemas = useCallback((res) => {
    const types = parseTypeOptions(res);
    const cache = schemaCacheRef.current;
    types.forEach((t) => {
      if (t?.certificateTypeId != null) cache.set(String(t.certificateTypeId), t);
    });
    return types;
  }, []);

  /** 按类型按需拉取填写规则（不空参全量拉，避免整店 schema 走一遍网络与内存）。 */
  const loadTypeSchemas = useCallback(({ typeId: wantId, typeCode: wantCode }) => {
    const body = {};
    if (wantId) body.certificateTypeIdList = [Number(wantId)];
    else if (wantCode) body.certificateTypeCodes = [wantCode];
    else return Promise.resolve([]);
    return sheinPlatformAPI.getCertificateSchemasDetail(shopId, body).then((res) => rememberSchemas(res));
  }, [shopId, rememberSchemas]);

  const applyTypeSchema = useCallback((typeSchema) => {
    applySchemaToForm(typeSchema, seed, isEdit, {
      setSchema,
      setTypeId,
      setTypeCode,
      setDimension,
      setPresetValues,
      setSrmValues,
    });
  }, [seed, isEdit]);

  const applyLocalSchemasResponse = useCallback((res, seedTypeId, seedCode) => {
    const types = rememberSchemas(res);
    setSchemaTypes(types);
    setSrmAgencies(parseSrmAgencies(res));
    let typeSchema = null;
    if (seedTypeId) {
      typeSchema = parseSchemaType(res, seedTypeId);
    }
    if (!typeSchema && seedCode) {
      typeSchema = types.find((t) => String(typeCodeOf(t)).toUpperCase() === String(seedCode).toUpperCase()) || null;
    }
    if (typeSchema) {
      applyTypeSchema(typeSchema);
    } else if (seedCode) {
      setTypeCode(seedCode);
    }
    return types;
  }, [rememberSchemas, applyTypeSchema]);

  // 打开弹窗：仅读本地已同步的填写规则，不自动拉平台
  useEffect(() => {
    if (!open || !shopId) return undefined;
    let cancelled = false;
    const seedTypeId = seed?.certificateTypeId != null ? String(seed.certificateTypeId) : '';
    const seedCode = seed?.certificateTypeCode || '';
    const seedFiles = Array.isArray(seed?.fileList)
      ? seed.fileList
        .filter((f) => f?.fileUrl)
        .map((f) => ({
          fileName: f.fileName || 'certificate',
          fileUrl: f.fileUrl,
          fileMd5: f.fileMd5 || f.certificateMd5 || '',
        }))
      : [];

    setTypeId(seedTypeId);
    setTypeCode(seedCode);
    setDimension(seed?.certificateDimension != null ? String(seed.certificateDimension) : '1');
    setFileName(seedFiles[0]?.fileName || seed?.fileName || '');
    setSchema(null);
    setSchemaTypes([]);
    setSrmAgencies([]);
    setPresetValues({});
    setSrmValues({});
    setFiles(seedFiles);
    setDragOver(false);
    setUploading(false);
    setSaving(false);
    setVerifying(false);
    setSyncingSchemas(false);
    setDownloadingTemplate(false);
    setSchemaLoading(true);
    schemaCacheRef.current = new Map();

    const pending = [];
    // 1) 空参只回类型摘要 + 检测机构（后端不再读 schema_json）
    pending.push(
      sheinPlatformAPI.getCertificateSchemasDetail(shopId, {})
        .then((res) => {
          if (cancelled) return;
          setSchemaTypes(parseTypeOptions(res));
          setSrmAgencies(parseSrmAgencies(res));
        })
        .catch(() => {
          if (!cancelled) {
            setSchemaTypes([]);
            setSrmAgencies([]);
          }
        }),
    );
    // 2) 种子类型只在命中时按需拉取该类型的完整规则
    if (seedTypeId || seedCode) {
      pending.push(
        loadTypeSchemas({ typeId: seedTypeId, typeCode: seedCode })
          .then((types) => {
            if (cancelled) return;
            const hit = seedTypeId
              ? types.find((t) => String(t.certificateTypeId) === String(seedTypeId))
              : types.find((t) => String(typeCodeOf(t)).toUpperCase() === String(seedCode).toUpperCase());
            if (hit) {
              applyTypeSchema(hit);
            } else {
              setSchema(null);
              if (seedCode) setTypeCode(seedCode);
            }
          })
          .catch(() => {
            if (!cancelled) setSchema(null);
          }),
      );
    }
    Promise.allSettled(pending).finally(() => {
      if (!cancelled) setSchemaLoading(false);
    });

    return () => { cancelled = true; };
  }, [open, shopId, seed, isEdit, loadTypeSchemas, applyTypeSchema]);

  // 切换类型：命中缓存直接用，缺失则按需拉取该类型规则（不再空参全量拉）
  useEffect(() => {
    if (!open || !typeId || schemaLoading) return undefined;
    if (schema && String(schema.certificateTypeId) === String(typeId)) return undefined;
    const cached = schemaCacheRef.current.get(String(typeId));
    if (cached) {
      applyTypeSchema(cached);
      return undefined;
    }
    let cancelled = false;
    setSchemaLoading(true);
    loadTypeSchemas({ typeId })
      .then((types) => {
        if (cancelled) return;
        const hit = types.find((t) => String(t.certificateTypeId) === String(typeId));
        if (hit) applyTypeSchema(hit);
        else setSchema(null);
      })
      .catch(() => {
        if (!cancelled) setSchema(null);
      })
      .finally(() => {
        if (!cancelled) setSchemaLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, typeId, schemaLoading, schema, loadTypeSchemas, applyTypeSchema]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  const presetFields = useMemo(() => {
    if (!schema) return [];
    return [
      ...(Array.isArray(schema.presetInfoList) ? schema.presetInfoList : []),
      ...(Array.isArray(schema.otherPresetInfoList) ? schema.otherPresetInfoList : []),
    ].filter((f) => Number(f?.inputType) !== 5);
  }, [schema]);

  const syncSchemas = async () => {
    if (!shopId || syncingSchemas) return;
    setSyncingSchemas(true);
    try {
      const res = await sheinPlatformAPI.syncCertificateSchemas(shopId);
      const schemas = res?.schemas || res;
      const types = applyLocalSchemasResponse(
        schemas,
        typeId || (seed?.certificateTypeId != null ? String(seed.certificateTypeId) : ''),
        typeCode || seed?.certificateTypeCode || '',
      );
      const count = Number(res?.synced) || types.length;
      showToast(count > 0 ? t('certLib.schemaSynced', { count }) : t('certLib.schemaEmpty'), count > 0 ? 'success' : 'warning');
    } catch (e) {
      showToast(e?.message || t('certLib.schemaSyncFailed'), 'error');
    } finally {
      setSyncingSchemas(false);
    }
  };

  const downloadFillTemplate = async () => {
    if (!shopId || downloadingTemplate) return;
    if (!typeId && !typeCode) {
      showToast(t('certLib.needType'), 'warning');
      return;
    }
    const fileModelUrl = String(schema?.fileModelUrl || '').trim();
    if (!fileModelUrl) {
      showToast(t('certLib.noDownload'), 'warning');
      return;
    }
    setDownloadingTemplate(true);
    try {
      const typeName = String(schema?.certificateType || schema?.certificateTypeName || typeCode || t('certLib.templateName')).trim();
      await sheinPlatformAPI.downloadCertificateSchemaFileModel(shopId, {
        certificateTypeId: typeId || undefined,
        certificateTypeCode: typeCode || undefined,
        fileName: typeName.toLowerCase().endsWith('.pdf') ? typeName : `${typeName}.pdf`,
      });
    } catch (e) {
      showToast(e?.message || t('certLib.downloadFailed'), 'error');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const resolveUploadFileName = (originalName, index) => {
    const custom = String(fileName || '').trim();
    if (!custom) return originalName || 'certificate';
    if (index === 0 && files.length === 0) return custom;
    const dot = custom.lastIndexOf('.');
    if (dot > 0) {
      return `${custom.slice(0, dot)}_${index + 1}${custom.slice(dot)}`;
    }
    return `${custom}_${index + 1}`;
  };

  const uploadFiles = async (fileList) => {
    if (!shopId || !fileList?.length) return;
    if (busy) return;
    const accepted = [];
    for (const file of fileList) {
      if (!isAllowedCertFile(file)) {
        showToast(t('certLib.badFile', { name: file.name }), 'error');
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        showToast(t('certLib.fileTooBig', { name: file.name }), 'error');
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (let i = 0; i < accepted.length; i += 1) {
        const file = accepted[i];
        // ② 上传资质证书文件：本地文件转平台 URL + MD5
        const res = await sheinPlatformAPI.uploadGoodsCertificateFile(shopId, file);
        const info = extractUploadInfo(res);
        if (!info.fileUrl || !info.fileMd5) {
          throw new Error(t('certLib.uploadMissing', { name: file.name }));
        }
        uploaded.push({
          fileName: resolveUploadFileName(file.name, files.length + i),
          fileUrl: info.fileUrl,
          fileMd5: info.fileMd5,
        });
      }
      setFiles((prev) => {
        const next = [...prev, ...uploaded];
        if (!String(fileName || '').trim() && next[0]?.fileName) {
          setFileName(next[0].fileName);
        }
        return next;
      });
      showToast(t('certLib.converted', { count: uploaded.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('certLib.uploadFailed'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const verifyByPoolSn = async (poolSn) => {
    const searchRes = await sheinPlatformAPI.searchGoodsCertificates(shopId, {
      pageNum: 1,
      pageSize: 10,
      poolSnList: [poolSn],
    });
    const row = findCertByPoolSn(searchRes, poolSn);
    if (!row) {
      throw new Error(t('certLib.verifyMissing', { no: poolSn }));
    }
    return row;
  };

  const submit = async () => {
    if (!shopId) return;
    const selected = typeOptions.find((t) => String(t.certificateTypeId) === String(typeId))
      || typeOptions.find((t) => String(typeCodeOf(t)).toUpperCase() === String(typeCode || '').toUpperCase());
    const code = (typeCode || typeCodeOf(selected) || typeCodeOf(schema) || '').trim();
    if (!isEdit && !code) {
      showToast(t('certLib.needTypeCode'), 'error');
      return;
    }
    if (!schema && !isEdit) {
      showToast(t('certLib.needSyncedType'), 'error');
      return;
    }
    if (!files.length) {
      showToast(t('certLib.needFile'), 'error');
      return;
    }
    const customName = String(fileName || '').trim();
    if (!customName) {
      showToast(t('certLib.needFileName'), 'error');
      return;
    }
    const fileListPayload = files.map((f, idx) => ({
      fileName: (idx === 0) ? customName : (f.fileName || `${customName}_${idx + 1}`),
      fileUrl: f.fileUrl,
      fileMd5: f.fileMd5,
    }));
    for (const f of fileListPayload) {
      if (!f.fileName || !f.fileUrl || !f.fileMd5) {
        showToast(t('certLib.fileIncomplete'), 'error');
        return;
      }
    }
    let presetInfoList = [];
    try {
      presetInfoList = buildPresetPayload(schema, presetValues, srmValues);
    } catch (e) {
      showToast(e?.message || t('certLib.needFields'), 'error');
      return;
    }
    const body = {
      certificateDimension: Number(dimension) || 1,
      fileList: fileListPayload,
      presetInfoList,
    };
    if (isEdit) {
      body.poolSn = seed?.poolSn;
    } else {
      body.certificateTypeCode = code;
    }
    setSaving(true);
    try {
      // ③ 创建/编辑资质证书
      const res = await sheinPlatformAPI.saveGoodsCertificate(shopId, body);
      const poolSn = extractSavePoolSn(res) || seed?.poolSn || '';
      const failMsg = res?.failMsg || res?.info?.failMsg;
      const existList = res?.existPoolSnList || res?.info?.existPoolSnList;
      if (failMsg) {
        throw new Error(failMsg);
      }
      if (Array.isArray(existList) && existList.length) {
        throw new Error(t('certLib.fileExists', { names: existList.join('、') }));
      }
      if (!poolSn) {
        throw new Error(t('certLib.noPoolSn'));
      }

      // ④ 按证书编号回查列表状态，确认创建成功
      setSaving(false);
      setVerifying(true);
      const verified = await verifyByPoolSn(poolSn);
      const statusText = statusLabel(verified?.status);
      showToast(
        isEdit
          ? t('certLib.updatedNo', { no: poolSn, status: statusText })
          : t('certLib.createdNo', { no: poolSn, status: statusText }),
        'success',
      );
      onDone?.(poolSn);
      onClose?.();
    } catch (e) {
      showToast(e?.message || t('certLib.saveFailed'), 'error');
    } finally {
      setSaving(false);
      setVerifying(false);
    }
  };

  if (!open) return null;

  const renderPresetField = (field) => {
    const key = String(field.presetId);
    const required = Number(field.isRequired) === 1;
    const labelText = cleanFieldLabel(field.presetRemark || field.presetName || key);
    const inputType = Number(field.inputType);

    if (isSrmField(field)) {
      const current = srmValues[key] || {};
      const agencyId = current.agencyId || '';
      const laboratoryId = current.laboratoryId || '';
      const agencyRow = srmAgencies.find(
        (item) => String(item?.detectionAgency?.detectionAgencyId) === String(agencyId),
      );
      const labs = Array.isArray(agencyRow?.laboratoryList) ? agencyRow.laboratoryList : [];
      return (
        <React.Fragment key={key}>
          <label className="spo-field">
            <FieldLabel required={required}>{labelText || t('certLib.agency')}</FieldLabel>
            <select
              className="spo-select"
              value={agencyId}
              disabled={busy}
              onChange={(e) => {
                const nextAgency = e.target.value;
                setSrmValues((prev) => ({
                  ...prev,
                  [key]: { agencyId: nextAgency, laboratoryId: '' },
                }));
              }}
            >
              <option value="">{t('certLib.pickAgency')}</option>
              {srmAgencies.map((item) => {
                const id = item?.detectionAgency?.detectionAgencyId;
                if (id == null) return null;
                return (
                  <option key={id} value={String(id)}>
                    {item?.detectionAgency?.detectionAgencyName || id}
                  </option>
                );
              })}
            </select>
          </label>
          <label className="spo-field">
            <FieldLabel required={required}>{t('certLib.lab')}</FieldLabel>
            <select
              className="spo-select"
              value={laboratoryId}
              disabled={busy || !agencyId}
              onChange={(e) => {
                const nextLab = e.target.value;
                setSrmValues((prev) => ({
                  ...prev,
                  [key]: { agencyId, laboratoryId: nextLab },
                }));
              }}
            >
              <option value="">{t('certLib.pickLab')}</option>
              {labs.map((lab) => (
                <option key={lab.laboratoryId} value={String(lab.laboratoryId)}>
                  {lab.laboratoryName || lab.laboratoryId}
                </option>
              ))}
            </select>
          </label>
        </React.Fragment>
      );
    }

    if (inputType === 1 || inputType === 2) {
      const options = Array.isArray(field.presetValueList) ? field.presetValueList : [];
      return (
        <label key={key} className="spo-field">
          <FieldLabel required={required}>{labelText}</FieldLabel>
          <select
            className="spo-select"
            value={presetValues[key] || ''}
            disabled={busy}
            onChange={(e) => setPresetValues((prev) => ({ ...prev, [key]: e.target.value }))}
          >
            <option value="">{t('certLib.pleasePick')}</option>
            {options.map((opt) => (
              <option key={opt.presetValueId} value={String(opt.presetValueId)}>
                {opt.presetValue || opt.presetValueId}
              </option>
            ))}
          </select>
        </label>
      );
    }

    if (inputType === 4) {
      return (
        <div key={key} className="spo-field">
          <FieldLabel required={required}>{labelText}</FieldLabel>
          <SheinDateTimePicker
            value={presetValues[key] || ''}
            disabled={busy}
            placeholder={t('certLib.pickDateTime')}
            ariaLabel={labelText}
            onChange={(next) => setPresetValues((prev) => ({ ...prev, [key]: next || '' }))}
          />
        </div>
      );
    }

    return (
      <label key={key} className="spo-field">
        <FieldLabel required={required}>{labelText}</FieldLabel>
        <input
          className="spo-input"
          type="text"
          value={presetValues[key] || ''}
          disabled={busy}
          onChange={(e) => setPresetValues((prev) => ({ ...prev, [key]: e.target.value }))}
        />
      </label>
    );
  };

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        style={{ width: 'min(720px, calc(100vw - 32px))' }}
      >
        <div className="spo-batch-modal__head">
          <h2>{title}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose} disabled={busy}>{t('moduleShell.close')}</button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          <div className="spo-cert-modal__toolbar" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={busy || !shopId}
              onClick={syncSchemas}
            >
              {syncingSchemas ? t('productAction.syncing') : t('certLib.syncSchema')}
            </button>
            <p className="spo-muted" style={{ margin: 0 }}>
              {t('certLib.syncSchemaHint')}
            </p>
          </div>

          <div className="spo-cert-modal__grid">
            <label className="spo-field">
              <FieldLabel required>{t('certLib.colScope')}</FieldLabel>
              <select
                className="spo-select"
                value={dimension}
                disabled={busy}
                onChange={(e) => setDimension(e.target.value)}
              >
                <option value="1">{t('certLib.scopePartial')}</option>
                <option value="2">{t('certLib.scopeAll')}</option>
              </select>
            </label>
            <div className="spo-field">
              <FieldLabel required>{t('certLib.typeCode')}</FieldLabel>
              <SearchableSelect
                options={typeSelectOptions}
                value={typeId}
                disabled={isEdit || busy || schemaLoading}
                placeholder={
                  schemaLoading
                    ? t('certLib.readingRules')
                    : (typeOptions.length ? t('certLib.searchType') : t('certLib.noLocalRules'))
                }
                emptyText={t('certLib.noTypeMatch')}
                onChange={(nextId) => {
                  setTypeId(nextId);
                  const selected = typeOptions.find((t) => String(t.certificateTypeId) === String(nextId));
                  setTypeCode(typeCodeOf(selected) || '');
                  setPresetValues({});
                  setSrmValues({});
                  setSchema(null);
                }}
              />
            </div>
            <label className="spo-field">
              <FieldLabel required>{t('certLib.fileNameLabel')}</FieldLabel>
              <input
                className="spo-input"
                value={fileName}
                disabled={busy}
                placeholder={t('certLib.fileNamePh')}
                onChange={(e) => {
                  const next = e.target.value;
                  setFileName(next);
                  setFiles((prev) => {
                    if (!prev.length) return prev;
                    const copy = [...prev];
                    copy[0] = { ...copy[0], fileName: next.trim() || copy[0].fileName };
                    return copy;
                  });
                }}
              />
            </label>
          </div>

          {typeCode ? (
            <p className="spo-muted">
              {t('certLib.currentType')}
              <code>{typeCode}</code>
              {typeId ? ` · typeId=${typeId}` : ''}
            </p>
          ) : null}

          {syncingSchemas ? (
            <p className="spo-muted">{t('certLib.syncingRules')}</p>
          ) : null}

          {typeId || typeCode ? (
            <p className="spo-muted">
              {t('certLib.certTemplate')}
              {String(schema?.fileModelUrl || '').trim() ? (
                <button
                  type="button"
                  className="spo-hint-link"
                  disabled={!shopId || downloadingTemplate}
                  onClick={downloadFillTemplate}
                >
                  {downloadingTemplate ? t('certLib.downloading') : t('certLib.downloadTemplate')}
                </button>
              ) : (
                <span>{t('certLib.noPlatformTemplate')}</span>
              )}
            </p>
          ) : null}

          {presetFields.length > 0 ? (
            <div className="spo-cert-modal__grid">
              {presetFields.map((field) => renderPresetField(field))}
            </div>
          ) : ((typeId || typeCode) && !schemaLoading && schema ? (
            <p className="spo-muted">{t('certLib.noExtraFields')}</p>
          ) : null)}

          <div className="spo-field spo-field--full spo-cert-upload-field">
            <FieldLabel required>{t('certLib.colFiles')}</FieldLabel>
            <div
              className={`spo-cert-dropzone${dragOver ? ' spo-cert-dropzone--over' : ''}${uploading ? ' is-busy' : ''}`}
              onDragEnter={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (uploading || saving || verifying || !shopId) return;
                uploadFiles(Array.from(e.dataTransfer?.files || []));
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_FILES}
                multiple
                className="spo-cert-dropzone__input"
                disabled={uploading || saving || verifying || !shopId}
                onChange={(e) => {
                  uploadFiles(Array.from(e.target.files || []));
                  e.target.value = '';
                }}
              />
              <span className="spo-cert-dropzone__placeholder">
                <span className="spo-cert-dropzone__plus">+</span>
                <span className="spo-cert-dropzone__title">{uploading ? t('certLib.uploading') : t('certLib.clickOrDrop')}</span>
                <span className="spo-cert-dropzone__hint">{t('certLib.fileHint')}</span>
              </span>
            </div>
            {files.length > 0 ? (
              <ul className="spo-cert-uploaded-list" aria-label={t('certLib.uploadedAria')}>
                {files.map((f, idx) => (
                  <li key={`${f.fileMd5 || f.fileUrl || f.fileName}-${idx}`} className="spo-cert-uploaded-item">
                    <FileText size={18} className="spo-cert-uploaded-item__icon" aria-hidden="true" />
                    <span className="spo-cert-uploaded-item__name" title={f.fileName}>
                      {f.fileName || t('certLib.unnamed')}
                    </span>
                    <button
                      type="button"
                      className="spo-btn spo-btn--ghost spo-btn--sm"
                      disabled={busy}
                      onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      {t('certLib.remove')}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose} disabled={busy}>{t('shop.cancel')}</button>
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={submit} disabled={busy || schemaLoading}>
            {verifying ? t('certLib.verifying') : (saving ? t('returnList.submitting') : (isEdit ? t('photoLib.saveChanges') : t('certLib.createCert')))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * 模板库 · 证书库：
 * 列表走 goods-certificates/search 本地缓存；
 * 新建/编辑：读本地填写规则；需手动「同步填写规则」写入本地后再选类型创建。
 */
export default function CertificateLibraryPage({
  syncKinds = ['certificateLibrary', 'certificateLibraryBatchCopy'],
} = {}) {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, pageShopScope, scopedShops = [] } = useSheinPageShopScope();
  const {
    authShopId,
    scopeTab,
    changeScopeTab,
    listShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    canAccessTeam,
  } = useSheinTemplateScope({ loadCategories: false, preferredShopId: pageShopScope });
  
  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: listShopId,
  });

  const ensureWriteShopId = useCallback(async ({
    title = t('productTemplate.pickShopTitle'),
    description = t('productTemplate.pickShopDesc'),
    confirmText = t('productTemplate.confirm'),
  } = {}) => resolveWriteShopId({
    listAllShops,
    pageShopScope,
    listShopId,
    pickShop,
    title,
    description,
    confirmText,
  }), [listAllShops, pageShopScope, listShopId, pickShop, t]);

const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [queryNonce, setQueryNonce] = useState(0);
  const [detailRow, setDetailRow] = useState(null);
  const [saveMode, setSaveMode] = useState(null);
  const [saveSeed, setSaveSeed] = useState(null);
  const [bindRow, setBindRow] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [batchCopying, setBatchCopying] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [moving, setMoving] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const loadList = useCallback(async () => {
    if (!canQuery || !listShopId) {
      setRows([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await sheinPlatformAPI.listCertificateLibrary(listShopId, {
        source: isOfficialTab ? 'official' : 'platform',
        scope: scopeTab,
        page,
        pageSize,
        keyword: keyword || undefined,
        certificateTypeId: typeFilter || undefined,
        status: statusFilter || undefined,
        ...(listAllShops ? { allShops: true } : {}),
      });
      setRows(Array.isArray(res?.list) ? res.list : []);
      setTotal(Number(res?.total) || 0);
      // 跨页选中：翻页刷新列表时保留已选
    } catch (e) {
      setRows([]);
      setTotal(0);
      showToast(e?.message || t('certLib.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [canQuery, listShopId, listAllShops, isOfficialTab, scopeTab, page, pageSize, keyword, typeFilter, statusFilter, queryNonce, t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!authShopId) {
      setTypeOptions([]);
      return;
    }
    sheinPlatformAPI.listCertificateTypes(authShopId)
      .then((res) => setTypeOptions(parseTypeOptions(res)))
      .catch(() => setTypeOptions([]));
  }, [authShopId]);

  useEffect(() => {
    setKeywordDraft('');
    setKeyword('');
    setTypeFilter('');
    setStatusFilter('');
    setPage(1);
    setQueryNonce((n) => n + 1);
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setDetailRow(null);
    setSaveMode(null);
    setSaveSeed(null);
    setBindRow(null);
    setSelectedKeys(new Set());
  }, [authShopId]);

  useEffect(() => {
    if (!authShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(authShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'CERTIFICATE_LIBRARY');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [authShopId]);

  const handleSync = useCallback(async () => {
    const writeShopId = await ensureWriteShopId({
      title: t('certLib.pickSyncShop'),
      description: t('certLib.pickSyncDesc'),
      confirmText: t('certLib.syncThisShop'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const track = await startJob({
      kind: 'certificateLibrary',
      shopId: writeShopId,
      label: t('certLib.syncUpdate'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const view = result.view || {};
          const count = Number(view.imported) || 0;
          const finishedAt = view.finishedAt || new Date().toISOString();
          setLastSyncedAt(String(finishedAt));
          setPersistedSyncedAt(String(finishedAt));
          showToast(view.message || t('certLib.syncDone', { count }), 'success');
          setPage(1);
          setQueryNonce((n) => n + 1);
        }
        resolve();
      });
    });
  }, [ensureWriteShopId, startJob, onTrackDone, t]);

  const {
    refresh: onSyncLibrary,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('certificateLibrary', authShopId);
  const certLibBatchCopying = isBusy('certificateLibraryBatchCopy', listShopId) || batchCopying;

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const emptyHint = useMemo(() => {
    if (!canQuery) return t('returnList.pickShop');
    if (loading) return t('returnList.loading');
    if (isOfficialTab) return t('certLib.emptyOfficial');
    if (!latestSyncedAt) return t('certLib.emptyPullShein');
    return t('certLib.noMatch');
  }, [canQuery, loading, isOfficialTab, latestSyncedAt, t]);

  const refreshAfterMutation = useCallback(() => {
    setQueryNonce((n) => n + 1);
  }, []);

  const onCopyOfficial = async (row, targetScope) => {
    if (!canQuery || !row?.id || copyingId) return;
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('certLib.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const resolvedTarget = resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab });
    setCopyingId(row.id);
    try {
      await sheinPlatformAPI.copyOfficialCertificateLibrary(
        writeShopId,
        {
          sourceId: row.id,
          sourceScope: SHEIN_TEMPLATE_SCOPES.official,
          targetScope: resolvedTarget,
          name: copyTemplateName(row.name),
        },
      );
      showToast(
        isTeamTemplateScope(resolvedTarget)
          ? t('certLib.copiedTeamShein')
          : t('certLib.copiedShopShein'),
        'success',
      );
      changeScopeTab(resolvedTarget, {
        onReset: () => {
          setPage(1);
          setQueryNonce((n) => n + 1);
        },
      });
      if (scopeTab === resolvedTarget) {
        setPage(1);
        setQueryNonce((n) => n + 1);
      }
    } catch (e) {
      showToast(e?.message || t('productTemplate.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onSyncToShein = async (row) => {
    if (!canQuery || !row?.id || !canSubmitOfficialRow(row) || syncingId) return;
    const shopId = resolveTemplateRowShopId(row, listShopId);
    setSyncingId(row.id);
    try {
      const res = await sheinPlatformAPI.prepareCertificateTemplateSync(shopId, row.id);
      const item = res?.item || {};
      setSaveSeed({
        ...item,
        localTemplateId: row.id,
        shopId: item.shopId || shopId,
      });
      setSaveMode('create');
      showToast(t('certLib.confirmSaveShein'), 'success');
    } catch (e) {
      showToast(e?.message || t('certLib.prepareFailed'), 'error');
    } finally {
      setSyncingId(null);
    }
  };

  const submitOfficialRowDirect = async (row) => {
    const shopId = resolveTemplateRowShopId(row, listShopId);
    if (!shopId || !row?.id) {
      throw new Error(t('certLib.missingShopTpl'));
    }
    const res = await sheinPlatformAPI.prepareCertificateTemplateSync(shopId, row.id);
    const item = res?.item || {};
    const code = String(item.certificateTypeCode || row.certificateTypeCode || '').trim();
    if (!code) {
      throw new Error(t('certLib.missingTypeCode', { name: row.name || row.id }));
    }
    const fileList = (Array.isArray(item.fileList) ? item.fileList : [])
      .filter((f) => f?.fileUrl && f?.fileMd5)
      .map((f, idx) => ({
        fileName: f.fileName || item.fileName || `certificate_${idx + 1}`,
        fileUrl: f.fileUrl,
        fileMd5: f.fileMd5,
      }));
    if (!fileList.length) {
      throw new Error(t('certLib.missingSubmitFile', { name: row.name || row.id }));
    }
    const body = {
      certificateTypeCode: code,
      certificateDimension: Number(item.certificateDimension ?? row.certificateDimension) || 1,
      fileList,
      presetInfoList: Array.isArray(item.presetInfoList)
        ? item.presetInfoList
        : (Array.isArray(row.presetInfoList) ? row.presetInfoList : []),
    };
    const saveRes = await sheinPlatformAPI.saveGoodsCertificate(shopId, body);
    const failMsg = saveRes?.failMsg || saveRes?.info?.failMsg;
    if (failMsg) {
      throw new Error(failMsg);
    }
    const existList = saveRes?.existPoolSnList || saveRes?.info?.existPoolSnList;
    if (Array.isArray(existList) && existList.length) {
      throw new Error(t('certLib.fileExists', { names: existList.join('、') }));
    }
    const poolSn = extractSavePoolSn(saveRes);
    if (!poolSn) {
      throw new Error(t('certLib.noPoolSn'));
    }
    await sheinPlatformAPI.markCertificateTemplateSynced(shopId, row.id, { poolSn });
    return poolSn;
  };

  const onBatchSubmitOfficial = async () => {
    if (!canQuery || selectedKeys.size === 0 || batchSubmitting) {
      showToast(t('certLib.pickSubmit'), 'warning');
      return;
    }
    const targets = resolveSelectedRows().filter((row) => canSubmitOfficialRow(row));
    if (!targets.length) {
      showToast(t('certLib.submitOfficialOnly'), 'warning');
      return;
    }
    const skipped = resolveSelectedRows().length - targets.length;
    const ok = await showSheinConfirm({
      title: t('certLib.batchSubmitTitle'),
      message: skipped > 0
        ? t('certLib.batchSubmitSkip', { count: targets.length, skipped })
        : t('certLib.batchSubmit', { count: targets.length }),
      confirmText: t('certLib.submit'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setBatchSubmitting(true);
    let success = 0;
    const failures = [];
    try {
      for (const row of targets) {
        try {
          await submitOfficialRowDirect(row);
          success += 1;
        } catch (e) {
          failures.push(`${row.name || row.id}：${e?.message || t('certLib.failedShort')}`);
        }
      }
      if (success > 0 && failures.length === 0) {
        showToast(t('certLib.submittedBackend', { count: success }), 'success');
      } else if (success > 0) {
        showToast(t('certLib.submitMixed', { ok: success, fail: failures.length, detail: failures[0] || '' }), 'warning');
      } else {
        showToast(failures[0] || t('certLib.batchSubmitFailed'), 'error');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } finally {
      setBatchSubmitting(false);
    }
  };

  const selectableKeys = useMemo(
    () => rows.map((row) => rowKey(row)).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resolveSelectedRows = () => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  };

  const writeLibraryCsv = (exportRows, filename) => {
    if (!exportRows.length) {
      showToast(t('certLib.noneExport'), 'warning');
      return;
    }
    downloadCsv(
      filename,
      ['poolSn', 'certificateType', 'files', 'source', 'status', 'dimension', 'bindSkc', 'effectiveTime', 'invalidTime', 'lastUpdateTime'],
      exportRows.map((row) => ({
        poolSn: row.poolSn || '',
        certificateType: row.certificateTypeName || row.certificateTypeCode || '',
        files: fileNamesOf(row).join('、'),
        source: sourceLabel(row, { isOfficialTab }),
        status: statusLabel(row.status, row),
        dimension: dimensionLabel(row.certificateDimension),
        bindSkc: bindSkcLabel(row),
        effectiveTime: row.effectiveTime || '',
        invalidTime: row.invalidTime || '',
        lastUpdateTime: row.lastUpdateTime || formatTime(row.lastSyncedAt),
      })),
    );
    showToast(t('certLib.exported', { count: exportRows.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!canQuery || !listShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await sheinPlatformAPI.listCertificateLibrary(listShopId, {
          source: isOfficialTab ? 'official' : 'platform',
          scope: scopeTab,
          page: pageNo,
          pageSize: size,
          keyword: keyword || undefined,
          certificateTypeId: typeFilter || undefined,
          status: statusFilter || undefined,
          ...(listAllShops ? { allShops: true } : {}),
        });
        return {
          items: Array.isArray(res?.list) ? res.list : [],
          total: Number(res?.total) || 0,
        };
      });
      writeLibraryCsv(all, `shein-certificate-library-filtered-${Date.now()}.csv`);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportPage = () => {
    writeLibraryCsv(rows, `shein-certificate-library-page-${Date.now()}.csv`);
  };

  const exportSelected = () => {
    const selected = resolveSelectedRows();
    if (!selected.length) {
      showToast(t('certLib.pickExport'), 'warning');
      return;
    }
    writeLibraryCsv(selected, `shein-certificate-library-selected-${Date.now()}.csv`);
  };

  const onBatchDelete = async () => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('certLib.pickDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .filter((row) => !row?.localTemplate)
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('certLib.missingLocalId'), 'error');
      return;
    }
    const ok = await showSheinConfirm({
      title: t('returnList.batchDelete'),
      message: t('certLib.batchDeleteMsg', { count: ids.length, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const grouped = new Map();
      resolveSelectedRows().forEach((row) => {
        if (row?.localTemplate) return;
        const id = Number(row?.id);
        if (!Number.isFinite(id) || id <= 0) return;
        const sid = resolveTemplateRowShopId(row, listShopId);
        if (!sid) return;
        if (!grouped.has(sid)) grouped.set(sid, []);
        grouped.get(sid).push(id);
      });
      let deleted = 0;
      for (const [sid, certIds] of grouped.entries()) {
        const res = await sheinPlatformAPI.batchDeleteCertificateLibrary(sid, certIds);
        const n = Number(res?.deleted);
        deleted += Number.isFinite(n) && n >= 0 ? n : certIds.length;
      }
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('certLib.deletedLocal', { count }), 'success');
      } else {
        showToast(t('certLib.noneDeleted'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await loadList();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchCopy = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('certLib.pickOfficialCopy'), 'warning');
      return;
    }
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('certLib.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdCopy'), 'error');
      return;
    }
    setBatchCopying(true);
    try {
      const track = await startJob({
        kind: 'certificateLibraryBatchCopy',
        shopId: writeShopId,
        label: t('certLib.copyJob', { count: ids.length }),
        silent: true,
        payload: {
          ids,
          sourceScope: SHEIN_TEMPLATE_SCOPES.official,
          targetScope,
        },
      });
      if (!track?.id) return;
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (result) => {
          unsub();
          const view = result?.view || {};
          const copied = Number(view.imported ?? view.copied) || 0;
          if (result?.ok) {
            showToast(
              view.message
                || t('certLib.copiedTo', { count: copied || ids.length, scope: isTeamTemplateScope(targetScope) ? t('certLib.scopeTeam') : t('certLib.scopeShop') }),
              'success',
            );
            changeScopeTab(targetScope, {
              onReset: () => {
                setPage(1);
                setQueryNonce((n) => n + 1);
              },
            });
            if (scopeTab === targetScope) {
              setPage(1);
              setQueryNonce((n) => n + 1);
            }
          } else {
            showToast(view?.error || view?.message || t('productTemplate.batchCopyFailed'), 'error');
          }
          resolve();
        });
      });
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchCopyFailed'), 'error');
    } finally {
      setBatchCopying(false);
    }
  };

  const onBatchMove = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('certLib.pickMove'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .filter((row) => row?.localTemplate)
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('certLib.moveLocalOnly'), 'error');
      return;
    }
    setMoving(true);
    try {
      const grouped = new Map();
      resolveSelectedRows().forEach((row) => {
        if (!row?.localTemplate) return;
        const id = Number(row?.id);
        if (!Number.isFinite(id) || id <= 0) return;
        const sid = resolveTemplateRowShopId(row, listShopId);
        if (!sid) return;
        if (!grouped.has(sid)) grouped.set(sid, []);
        grouped.get(sid).push(id);
      });
      let moved = 0;
      for (const [sid, certIds] of grouped.entries()) {
        const res = await sheinPlatformAPI.moveCertificateLibraryTemplates(sid, {
          ids: certIds,
          targetScope,
        });
        const n = Number(res?.moved);
        moved += Number.isFinite(n) && n >= 0 ? n : certIds.length;
      }
      showToast(t('productTemplate.movedCount', { count: moved }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveFailed'), 'error');
    } finally {
      setMoving(false);
    }
  };

  return (
    <>
      <SheinSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || deleting || certLibBatchCopying || moving || batchSubmitting || exporting}
            batchCopying={certLibBatchCopying}
            batchMoving={moving}
            batchOpsMenu={buildSheinWorkspaceBatchOpsMenu({
              scopeTab,
              isOfficialTab,
              canAccessTeam,
              selectedCount: selectedKeys.size,
              onCopy: onBatchCopy,
              onMove: onBatchMove,
              onDelete: onBatchDelete,
              deleteDisabled: !canQuery || deleting || isOfficialTab,
              onSubmitOfficial: onBatchSubmitOfficial,
              submitOfficialDisabled: !canQuery || batchSubmitting || isOfficialTab,
            })}
            syncKinds={syncKinds}
            quotaKind="certificate-library"
            onSync={() => {
              onSyncLibrary();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!canQuery || syncing || isOfficialTab}
            lastSyncedLabel={isOfficialTab ? '' : latestSyncedLabel}
            syncHint={t('certLib.syncHintShein')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!canQuery || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            extraActions={isOfficialTab ? [] : [
              {
                key: 'create',
                label: t('certLib.create'),
                tone: 'primary',
                disabled: !canQuery,
                onClick: async () => {
                  const writeShopId = await ensureWriteShopId({
                    title: t('productTemplate.createPickTitle'),
                    description: t('certLib.createPickDesc'),
                    confirmText: t('productTemplate.createHere'),
                  });
                  if (!writeShopId) {
                    showToast(t('returnList.pickShop'), 'warning');
                    return;
                  }
                  setSaveSeed({ shopId: writeShopId });
                  setSaveMode('create');
                },
              },
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('certLib.keyword')}</span>
              <input
                className="spo-input"
                value={keywordDraft}
                disabled={!canQuery}
                placeholder={t('certLib.keywordPh')}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setKeyword(keywordDraft.trim());
                    setPage(1);
                  }
                }}
              />
            </div>
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('certLib.colType')}</span>
              <select
                className="spo-select"
                value={typeFilter}
                disabled={!canQuery || loading}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('certLib.allTypes')}</option>
                {typeOptions.map((item) => (
                  <option key={item.certificateTypeId} value={String(item.certificateTypeId)}>
                    {typeOptionLabel(item)}
                  </option>
                ))}
              </select>
            </div>
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('certLib.colStatus')}</span>
              <select
                className="spo-select"
                value={statusFilter}
                disabled={!canQuery || loading}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.value ? t('certLib.st' + opt.value) : t('certLib.statusAll')}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="spo-filter-bar__actions">
            <button
              type="button"
              className="spo-btn spo-btn--ghost spo-btn--sm"
              disabled={!canQuery || loading}
              onClick={() => {
                setKeywordDraft('');
                setKeyword('');
                setTypeFilter('');
                setStatusFilter('');
                setPage(1);
                setQueryNonce((n) => n + 1);
              }}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!canQuery || loading}
              onClick={() => {
                if (canQuery) setLoading(true);
                setKeyword(keywordDraft.trim());
                setPage(1);
                setQueryNonce((n) => n + 1);
              }}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard loading={Boolean(canQuery) && loading} loadingLabel={t('certLib.querying')}>
        {shopDimensionTabs}
        <SheinTemplateScopeTabs
          scopeTab={scopeTab}
          canAccessTeam={canAccessTeam}
          disabled={!canQuery || loading}
          ariaLabel={t('certLib.scopeAria')}
          onChange={(tabId) => {
            changeScopeTab(tabId, {
              onReset: () => {
                setPage(1);
                setSelectedKeys(new Set());
                selectedRowsRef.current.clear();
                setQueryNonce((n) => n + 1);
              },
            });
          }}
        />
        <div className="spo-drafts__table-wrap spo-certs-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-certs-table spo-cert-lib-table">
            <colgroup>
              {TABLE_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  className={`spo-certs-col spo-certs-col--${col.key}`}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-certs-th spo-certs-th--check"
                  onClick={() => {
                    if (!canQuery || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('certLib.selectAll')}
                    disabled={!canQuery || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {TABLE_COLUMNS.filter((c) => c.key !== 'check').map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`spo-certs-th spo-certs-th--${col.key}`}
                  >
                    {col.key === 'poolSn' ? (
                      <span className="spo-certs-th__with-icon">
                        <FileText size={14} className="spo-certs-th__pdf" aria-hidden="true" />
                        {i18n.t({ poolSn: 'certLib.colNo', type: 'certLib.colType', files: 'certLib.colFiles', source: 'certLib.colSource', status: 'certLib.colStatus', dimension: 'certLib.colScope', bindSkc: 'certLib.colBind', time: 'certLib.colTime', updatedAt: 'certLib.colUpdated', ops: 'certLib.colOps' }[col.key], { ns: 'workbench', defaultValue: col.label })}
                      </span>
                    ) : col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!canQuery || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_SPAN} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const names = fileNamesOf(row);
                  const isLocal = Boolean(row.localTemplate);
                  return (
                    <tr
                      key={key}
                      className="spo-row--clickable"
                      onClick={(e) => {
                        if (e.target.closest('button, a, input, .spo-copy-hover')) return;
                        toggleOne(key);
                      }}
                    >
                      <td className="spo-certs-td spo-certs-td--check">
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: row.poolSn || row.name || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="spo-certs-td" title={row.poolSn || row.name || ''}>
                        <strong>{row.poolSn || row.name || '—'}</strong>
                      </td>
                      <td className="spo-certs-td" title={row.certificateTypeName || ''}>
                        {row.certificateTypeName || row.certificateTypeCode || '—'}
                      </td>
                      <td className="spo-certs-td" title={names.join('、')}>
                        {names.length === 0
                          ? '—'
                          : names.length === 1
                            ? names[0]
                            : t('certLib.moreFiles', { name: names[0], count: names.length })}
                      </td>
                      <td className="spo-certs-td">
                        {sourceLabel(row, { isOfficialTab })}
                      </td>
                      <td className="spo-certs-td">
                        <span className={`spo-records__status spo-records__status--${statusModifier(row.status)}`}>
                          {statusLabel(row.status, row)}
                        </span>
                      </td>
                      <td className="spo-certs-td">{dimensionLabel(row.certificateDimension)}</td>
                      <td className="spo-certs-td">{bindSkcLabel(row)}</td>
                      <td className="spo-certs-td spo-certs-td--time">
                        <div className="spo-certs-time">
                          <div className="spo-certs-time__row">
                            <span className="spo-certs-time__label">{t('certLib.effective')}</span>
                            <span className="spo-certs-time__value">{row.effectiveTime || '—'}</span>
                          </div>
                          <div className="spo-certs-time__row">
                            <span className="spo-certs-time__label">{t('certLib.invalid')}</span>
                            <span className="spo-certs-time__value">{row.invalidTime || '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="spo-certs-td">{row.lastUpdateTime || formatTime(row.lastSyncedAt || row.updatedAt)}</td>
                      <td className="spo-certs-td spo-certs-td--ops spo-certs-td--actions">
                        <button
                          type="button"
                          className="spo-btn spo-btn--ghost spo-btn--sm"
                          onClick={() => setDetailRow(row)}
                        >
                          {t('certLib.filesBtn')}
                        </button>
                        {isOfficialTab && rowCanCopyScopedTemplate(row) ? (
                          <SheinOpsCopyHoverMenu
                            disabled={copyingId != null}
                            busy={copyingId === row.id}
                            options={buildTemplateCopyMenuOptions({
                              scopeTab,
                              isOfficialTab,
                              canAccessTeam,
                              onCopy: (target) => onCopyOfficial(row, target),
                            })}
                          />
                        ) : null}
                        {canSubmitOfficialRow(row) ? (
                          <button
                            type="button"
                            className="spo-btn spo-btn--ghost spo-btn--sm"
                            disabled={syncingId != null || batchSubmitting}
                            onClick={() => onSyncToShein(row)}
                          >
                            {syncingId === row.id ? t('returnList.submitting') : t('certLib.submitBackend')}
                          </button>
                        ) : null}
                        {!isOfficialTab && !isLocal ? (
                          <button
                            type="button"
                            className="spo-btn spo-btn--ghost spo-btn--sm"
                            onClick={() => {
                              setSaveSeed(row);
                              setSaveMode('edit');
                            }}
                          >
                            {t('productTemplate.edit')}
                          </button>
                        ) : null}
                        {!isOfficialTab && !isLocal && Number(row.certificateDimension) === 1 ? (
                          <button
                            type="button"
                            className="spo-btn spo-btn--ghost spo-btn--sm"
                            onClick={() => setBindRow(row)}
                          >
                            {t('certLib.bind')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!canQuery}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('certLib.pager')}
        />
      </ProductListCard>

      <FileDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />
      <SaveCertificateModal
        open={Boolean(saveMode)}
        mode={saveMode || 'create'}
        shopId={resolveTemplateRowShopId(saveSeed, authShopId)}
        typeOptions={typeOptions}
        seed={saveSeed}
        onClose={() => {
          setSaveMode(null);
          setSaveSeed(null);
        }}
        onDone={async (poolSn) => {
          if (saveSeed?.localTemplateId && poolSn) {
            try {
              await sheinPlatformAPI.markCertificateTemplateSynced(
                resolveTemplateRowShopId(saveSeed, authShopId),
                saveSeed.localTemplateId,
                { poolSn },
              );
            } catch {
              // 平台已创建成功，本地标记失败不阻断
            }
          }
          refreshAfterMutation();
        }}
      />
      <CertificateBindProductModal
        open={Boolean(bindRow)}
        shopId={resolveTemplateRowShopId(bindRow, authShopId)}
        row={bindRow}
        onClose={() => setBindRow(null)}
        onDone={refreshAfterMutation}
      />
      {shopPickModal}
    </>
  );
}
